/**
 * useActionExecutor — Internal action execution engine for DynamicTableV2.
 *
 * Resolves payload placeholders, calls endpoints, handles file downloads/uploads,
 * navigates, and triggers post-action lifecycle (toast, refetch, removeRow).
 *
 * All variable substitution is delegated to the shared `interpolation` module —
 * see utils/interpolation.ts for the full token reference.
 */

import { useCallback, useRef } from 'react';
import type { BackgroundJobConfig, InternalActionDef, OnSuccessAction } from '../core/schema/types';
import { getToken } from '../utils/getToken';
import { resolveTemplate, resolveValue } from '../utils/interpolation';
import type { TemplateContext } from '../utils/interpolation';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Passed to `onBackgroundJob` (and used internally by the built-in modal) when
 * an API response contains `{ sentToBackground: true }`.
 */
export interface BackgroundJobResult {
  /** Task reference returned by the server, e.g. `"TRF-20240413-00123"`. May be empty. */
  taskReferenceNo: string;
  /** The resolved `backgroundJobConfig.navigatePath` URL (all tokens already substituted). */
  navigatePath: string | null;
  /** The resolved dialog title (defaults applied). */
  title: string;
  /** The resolved dialog message (defaults applied). */
  message: string;
  /** The resolved primary-button label. */
  confirmLabel: string;
  /** Whether to display the task reference number below the message. */
  showTaskReferenceNo: boolean;
  /** The full raw API response data, if you need extra fields. */
  responseData: Record<string, unknown>;
}

export interface ActionContext {
  row?: Record<string, unknown>;
  selectedKeys?: string[];
  actionDef: InternalActionDef;
  selectAllActive?: boolean;
  deselectedRowIds?: string[];
  tableQuery?: Record<string, unknown>;
  /** Total server-side record count — used to resolve {count} when Select All Pages is active */
  totalCount?: number;
  /** Consumer-supplied env/config tokens, available as `{env.KEY}` in schema strings. */
  envTokens?: Record<string, string>;
}

/** Convert an ActionContext to the shared TemplateContext used by the interpolation module. */
function toTemplateContext(context: ActionContext): TemplateContext {
  return {
    row: context.row,
    selectedKeys: context.selectedKeys,
    selectAllActive: context.selectAllActive,
    deselectedRowIds: context.deselectedRowIds,
    totalCount: context.totalCount,
    tableQuery: context.tableQuery,
    envTokens: context.envTokens,
  };
}

/** Shallow-merge select-all `backgroundJobConfig` over the action-level config. */
function mergeBackgroundJobConfig(
  base: Readonly<BackgroundJobConfig> | null,
  override: Readonly<BackgroundJobConfig> | null,
): Readonly<BackgroundJobConfig> | null {
  if (!base && !override) return null;
  if (!override) return base;
  if (!base) return override;
  return { ...base, ...override };
}

export interface ActionExecutorConfig {
  axiosInstance?: { get: Function; post: Function; put: Function; delete: Function; request: Function };
  /**
   * React Router `navigate` function.
   * Accepts an optional second argument `{ state }` for router location state,
   * matching React Router v6's `NavigateFunction` signature.
   */
  navigate?: (path: string, options?: { state?: Record<string, unknown> }) => void;
  onRefetch?: () => void;
  onRemoveRow?: (rowId: string) => void;
  onToast?: (message: string, type: 'success' | 'error') => void;
  onClearSelection?: () => void;
  onActionIntercept?: (action: InternalActionDef, context: ActionContext) => boolean | Promise<boolean>;
  rowIdField?: string;
  /** Called by the 'startEditing' built-in action to put a row into inline-edit mode. */
  onStartEditing?: (rowKey: string, row: Record<string, unknown>) => void;
  /** Called by the 'startEditingAll' built-in action to put ALL rows into inline-edit mode. */
  onStartEditingAll?: () => void;
  /**
   * Consumer-supplied env/config tokens available as `{env.KEY}` in all schema strings.
   * Use for runtime values that are not part of row data (e.g. external app URLs).
   * ```tsx
   * <DynamicTableV2 envTokens={{ participantViewUrl: process.env.REACT_APP_PARTICIPANT_VIEW_URL }} />
   * ```
   */
  envTokens?: Record<string, string>;
  /**
   * Called when an action with `onSuccess: "backgroundJob"` receives an API
   * response with `sentToBackground: true`. The table passes the resolved
   * background-job details so the consumer (or the table's built-in modal) can
   * present a "submitted for processing" experience.
   *
   * When provided, the built-in background-job modal is suppressed; the
   * consumer takes full ownership.  When omitted, the table shows its own modal.
   */
  onBackgroundJob?: (result: BackgroundJobResult, action: InternalActionDef, context: ActionContext) => void;
}

export interface PendingConfirmation {
  action: InternalActionDef;
  context: ActionContext;
  /** Returns a promise that resolves when the underlying action completes (or throws). */
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useActionExecutor(config: ActionExecutorConfig) {
  const pendingConfirmRef = useRef<PendingConfirmation | null>(null);
  const executingRef = useRef(false);
  /** Set when a `backgroundJob` action completes with `sentToBackground: true`. Cleared by the table after the modal is dismissed. */
  const pendingBackgroundJobRef = useRef<BackgroundJobResult | null>(null);

  const makeRequest = useCallback(
    async (
      method: string,
      endpoint: string,
      payload: Record<string, unknown> | null,
      responseType?: string
    ) => {
      const path = endpoint.replace(/^\//, '');

      if (config.axiosInstance) {
        // axiosInstance has baseURL + interceptors (auth, tenant headers) — just pass the path.
        return config.axiosInstance.request({
          url: path,
          method: method.toLowerCase(),
          data: payload,
          ...(responseType ? { responseType } : {}),
        });
      }

      // Fallback: native fetch with manual auth
      const token = getToken();
      const baseUrl = process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '';
      const url = endpoint.startsWith('http') ? endpoint : `${baseUrl}/${path}`;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const fetchOptions: RequestInit = {
        method: method.toUpperCase(),
        headers,
      };
      if (payload && method.toUpperCase() !== 'GET') {
        fetchOptions.body = JSON.stringify(payload);
      }
      const response = await fetch(url, fetchOptions);
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      return response;
    },
    [config.axiosInstance]
  );

  const handleOnSuccess = useCallback(
    (
      action: InternalActionDef,
      context: ActionContext,
      /** Raw API response data — used by `onSuccess: "openTab"` / `backgroundJob` to read response fields. */
      responseData?: Record<string, unknown>,
      /**
       * When `true`, post-success behavior reads optional overrides from `action.selectAll`
       * (`onSuccess`, `successMessage`, `responseUrl`, `backgroundJobConfig`) so normal
       * bulk selection and "Select All Pages" can differ (e.g. toast vs background job).
       */
      useSelectAllPath = false,
    ) => {
      const sa = useSelectAllPath && action.selectAll ? action.selectAll : null;
      const behavior: OnSuccessAction = sa?.onSuccess ?? action.onSuccess;
      const rawSuccessMsg =
        sa?.successMessage != null && sa.successMessage !== ''
          ? sa.successMessage
          : action.successMessage;
      const successMsg = rawSuccessMsg ?? `${action.label} completed successfully`;
      const resolvedMsg = resolveTemplate(successMsg, toTemplateContext(context));
      const effectiveResponseUrl = sa?.responseUrl ?? action.responseUrl;
      const effectiveBgJobConfig = mergeBackgroundJobConfig(
        action.backgroundJobConfig,
        sa?.backgroundJobConfig ?? null,
      );

      switch (behavior) {
        case 'refetch':
          config.onRefetch?.();
          break;
        case 'toast':
          config.onToast?.(resolvedMsg, 'success');
          break;
        case 'toastAndRefetch':
          config.onToast?.(resolvedMsg, 'success');
          config.onRefetch?.();
          break;
        case 'removeRow':
          if (context.row) {
            const rowId = String(context.row._resolvedRowId ?? context.row[config.rowIdField ?? 'id'] ?? '');
            config.onRemoveRow?.(rowId);
          }
          break;
        case 'openTab': {
          if (!effectiveResponseUrl) break;
          // Build a combined context: row tokens + {response.field} substitution
          let url = resolveTemplate(effectiveResponseUrl, toTemplateContext(context));
          if (responseData) {
            url = url.replace(
              /\{response\.(\w+)\}/g,
              (_, field) => String(responseData[field] ?? ''),
            );
          }
          window.open(url, '_blank', 'noopener,noreferrer');
          break;
        }
        case 'backgroundJob': {
          const sentToBackground = responseData
            ? Boolean((responseData as any).sentToBackground)
            : false;

          if (sentToBackground) {
            const cfg = effectiveBgJobConfig;
            const taskRef = String((responseData as any).taskReferenceNo ?? '');
            const tctx = toTemplateContext(context);

            // Build a mini context that includes {response.*} for template substitution
            const resolveWithResponse = (template: string): string => {
              let out = resolveTemplate(template, tctx);
              if (responseData) {
                out = out.replace(/\{response\.(\w+)\}/g, (_, f) => String(responseData[f] ?? ''));
              }
              return out;
            };

            const title = resolveWithResponse(cfg?.title ?? 'Request Submitted');
            const message = resolveWithResponse(
              cfg?.message ?? 'Your request has been submitted for background processing.'
            );
            const confirmLabel = resolveWithResponse(cfg?.confirmLabel ?? 'View Details');
            const showRef =
              cfg?.showTaskReferenceNo !== undefined
                ? cfg.showTaskReferenceNo
                : taskRef.length > 0;
            const navigatePath = cfg?.navigatePath
              ? resolveWithResponse(cfg.navigatePath)
              : null;

            const result: BackgroundJobResult = {
              taskReferenceNo: taskRef,
              navigatePath,
              title,
              message,
              confirmLabel,
              showTaskReferenceNo: showRef,
              responseData: responseData ?? {},
            };

            if (config.onBackgroundJob) {
              // Consumer takes full control
              config.onBackgroundJob(result, action, context);
            } else {
              // Built-in modal — set ref; DynamicTableV2 listens via a state update
              pendingBackgroundJobRef.current = result;
            }
            // Always refetch after a background job
            config.onRefetch?.();
          } else {
            // Not background — treat as a normal toast + refetch
            config.onToast?.(resolvedMsg, 'success');
            config.onRefetch?.();
          }
          break;
        }
        case 'none':
        default:
          break;
      }

      if (context.selectedKeys && context.selectedKeys.length > 0) {
        config.onClearSelection?.();
      }
    },
    [config]
  );

  const handleOnError = useCallback(
    (action: InternalActionDef, context: ActionContext, error: unknown) => {
      const errorMsg =
        action.errorMessage ?? `Failed to ${action.label.toLowerCase()}`;
      const resolvedMsg = resolveTemplate(errorMsg, toTemplateContext(context));
      config.onToast?.(resolvedMsg, 'error');
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error(`[DynamicTableV2] Action "${action.label}" failed:`, error);
      }
    },
    [config]
  );

  const executeDirectAction = useCallback(
    async (action: InternalActionDef, context: ActionContext) => {
      const useSelectAll = context.selectAllActive && action.selectAll;
      const effectiveEndpoint = useSelectAll ? action.selectAll!.endpoint : action.endpoint;
      const effectiveMethod = useSelectAll ? action.selectAll!.method : action.method;
      const effectivePayloadTemplate = useSelectAll
        ? action.selectAll!.payload as Record<string, unknown> | null
        : action.payload as Record<string, unknown> | null;

      if (!effectiveEndpoint) return;
      const tctx = toTemplateContext(context);
      const endpoint = resolveTemplate(effectiveEndpoint, tctx);
      const payload = effectivePayloadTemplate
        ? resolveValue(effectivePayloadTemplate, tctx) as Record<string, unknown>
        : null;
      try {
        const response = await makeRequest(effectiveMethod, endpoint, payload);
        // Extract response body so openTab can use {response.field} tokens
        const responseData: Record<string, unknown> | undefined =
          response?.data ?? (typeof response?.json === 'function' ? await response.json() : undefined);
        handleOnSuccess(action, context, responseData, useSelectAll);
      } catch (error) {
        handleOnError(action, context, error);
      }
    },
    [makeRequest, handleOnSuccess, handleOnError]
  );

  const executeExportAction = useCallback(
    async (action: InternalActionDef, context: ActionContext) => {
      const useSelectAll = context.selectAllActive && action.selectAll;
      const effectiveEndpoint = useSelectAll ? action.selectAll!.endpoint : action.endpoint;
      const effectiveMethod = useSelectAll ? action.selectAll!.method : action.method;
      const effectivePayloadTemplate = useSelectAll
        ? action.selectAll!.payload as Record<string, unknown> | null
        : action.payload as Record<string, unknown> | null;

      if (!effectiveEndpoint) return;
      const tctx = toTemplateContext(context);
      const endpoint = resolveTemplate(effectiveEndpoint, tctx);
      const payload = effectivePayloadTemplate
        ? resolveValue(effectivePayloadTemplate, tctx) as Record<string, unknown>
        : null;
      try {
        const response = await makeRequest(effectiveMethod, endpoint, payload, 'blob');
        const blob = config.axiosInstance ? response.data : await response.blob();
        const url = window.URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;

        let resolvedFileName = action.fileName
          ? resolveTemplate(action.fileName, tctx)
          : 'download';

        const contentDisposition = response?.headers?.['content-disposition'] as string | undefined;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+?)"?(?:;|$)/);
          if (match?.[1]) resolvedFileName = match[1];
        }

        link.download = resolvedFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        handleOnSuccess(action, context, undefined, useSelectAll);
      } catch (error) {
        handleOnError(action, context, error);
      }
    },
    [makeRequest, config.axiosInstance, handleOnSuccess, handleOnError]
  );

  const executeNavigateAction = useCallback(
    (action: InternalActionDef, context: ActionContext) => {
      if (!action.path) return;
      const tctx = toTemplateContext(context);
      const path = resolveTemplate(action.path, tctx);

      // Resolve state via resolveValue so typed sentinels work:
      // `{selectedRowIds}` → string[], `{selectAllActive}` → boolean, etc.
      const resolvedState = action.state
        ? resolveValue(action.state, tctx) as Record<string, unknown>
        : undefined;

      config.navigate?.(path, resolvedState ? { state: resolvedState } : undefined);
    },
    [config]
  );

  const executeNewTabAction = useCallback(
    (action: InternalActionDef, context: ActionContext) => {
      const urlTemplate = action.url ?? action.endpoint;
      if (!urlTemplate) return;
      const url = resolveTemplate(urlTemplate, toTemplateContext(context));
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    []
  );

  const executeImportAction = useCallback(
    async (action: InternalActionDef, _context: ActionContext) => {
      if (!action.endpoint) return;
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';

      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        const formData = new FormData();
        formData.append(action.fileKey ?? 'File', file);

        const path = action.endpoint!.replace(/^\//, '');

        try {
          if (config.axiosInstance) {
            // axiosInstance handles baseURL + auth headers via interceptors.
            // Only set Content-Type for multipart — interceptor handles the rest.
            await config.axiosInstance.post(path, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          } else {
            const token = getToken();
            const baseUrl = process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '';
            const url = action.endpoint!.startsWith('http') ? action.endpoint! : `${baseUrl}/${path}`;
            const headers: Record<string, string> = {};
            if (token) headers.Authorization = `Bearer ${token}`;
            const resp = await fetch(url, { method: 'POST', headers, body: formData });
            if (!resp.ok) throw new Error(`Import failed: ${resp.status}`);
          }
          handleOnSuccess(action, _context);
        } catch (error) {
          handleOnError(action, _context, error);
        }
      };

      input.click();
    },
    [config.axiosInstance, handleOnSuccess, handleOnError]
  );

  const executeAction = useCallback(
    async (action: InternalActionDef, context: ActionContext) => {
      if (executingRef.current) return;

      if (config.onActionIntercept) {
        const proceed = await Promise.resolve(config.onActionIntercept(action, context));
        if (!proceed) return;
      }

      if (context.row && config.rowIdField) {
        context.row._resolvedRowId = String(context.row[config.rowIdField] ?? '');
      }

      // Propagate env tokens so all interpolation helpers have access
      if (config.envTokens && !context.envTokens) {
        context.envTokens = config.envTokens;
      }

      const doExecute = async () => {
        executingRef.current = true;
        try {
          switch (action.actionType) {
            case 'direct':
              await executeDirectAction(action, context);
              break;
            case 'export':
              await executeExportAction(action, context);
              break;
            case 'navigate':
              executeNavigateAction(action, context);
              break;
            case 'newTab':
              executeNewTabAction(action, context);
              break;
            case 'import':
              await executeImportAction(action, context);
              break;
            case 'modal':
              config.onToast?.(`${action.label}: Modal actions require onActionIntercept`, 'error');
              break;
            case 'startEditing':
              if (context.row && config.onStartEditing) {
                const rowKey = String(context.row._resolvedRowId ?? context.row[config.rowIdField ?? 'id'] ?? '');
                config.onStartEditing(rowKey, context.row);
              }
              break;
            case 'startEditingAll':
              config.onStartEditingAll?.();
              break;
            default:
              break;
          }
        } finally {
          executingRef.current = false;
        }
      };

      if (action.dialog) {
        pendingConfirmRef.current = {
          action,
          context,
          onConfirm: () => {
            pendingConfirmRef.current = null;
            return doExecute();
          },
          onCancel: () => {
            pendingConfirmRef.current = null;
          },
        };
        return pendingConfirmRef.current;
      }

      await doExecute();
      return null;
    },
    [
      config,
      executeDirectAction,
      executeExportAction,
      executeNavigateAction,
      executeNewTabAction,
      executeImportAction,
    ]
  );

  const executeSave = useCallback(
    async (
      editedRows: unknown[],
      saveEndpoint: string,
      saveMethod: string,
      onSaveSuccess: OnSuccessAction,
      customSuccessMessage?: string | null,
    ): Promise<{ ok: boolean; errors?: Array<{ sNo: string | null; field: string; message: string; uniqueIdentifier: string | null }> }> => {
      try {
        const saveResponse = await makeRequest(saveMethod, saveEndpoint, editedRows as unknown as Record<string, unknown>);
        const responseData = saveResponse?.data ?? saveResponse;

        // Treat Wealthlane-style { success: false, errors: [...] } as a business error
        if (responseData && typeof responseData === 'object' && (responseData as any).success === false) {
          const errorObj = responseData as any;
          const errorMsg = errorObj.message ?? 'Failed to save changes';
          const fieldErrors: Array<{ sNo: string | null; field: string; message: string; uniqueIdentifier: string | null }> = Array.isArray(errorObj.errors)
            ? errorObj.errors.map((e: any) => ({
                sNo: e.sNo ?? null,
                field: String(e.fieldName ?? e.field ?? e.propertyName ?? ''),
                message: String(e.error ?? e.message ?? e.errorMessage ?? ''),
                uniqueIdentifier: e.uniqueIdentifier ?? null,
              }))
            : [];
          const hasInlineErrors = fieldErrors.some((e) => e.field);
          if (!hasInlineErrors) {
            config.onToast?.(errorMsg, 'error');
          }
          return { ok: false, errors: fieldErrors };
        }

        const successMsg = customSuccessMessage ?? `${editedRows.length} row(s) saved successfully`;
        switch (onSaveSuccess) {
          case 'refetch':
            config.onRefetch?.();
            break;
          case 'toast':
            config.onToast?.(successMsg, 'success');
            break;
          case 'toastAndRefetch':
            config.onToast?.(successMsg, 'success');
            config.onRefetch?.();
            break;
          case 'none':
          default:
            break;
        }
        return { ok: true };
      } catch (error) {
        config.onToast?.('Failed to save changes', 'error');
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('[DynamicTableV2] Save failed:', error);
        }
        return { ok: false };
      }
    },
    [makeRequest, config]
  );

  return {
    executeAction,
    executeSave,
    pendingConfirmRef,
    pendingBackgroundJobRef,
  };
}
